/**
 * Act on a sign-out the moment this device reads it, not on its next launch.
 *
 * ⚠️ WHAT WAS WRONG. "Sign out all other devices" commits a rotation: the
 * bundle moves to a new key and every other device is REVOKED with its
 * wrapping cleared. The device that was signed out finds this out when
 * `reconcileLocalKeys` runs — and until now that ran only from the enrolment
 * path (a launch, an entry to the Hub). A phone sitting on a lesson, or on the
 * Devices screen watching its own six-second poll, read status after status
 * saying it was signed out and did nothing about any of them: the notes it
 * had decrypted stayed on screen, and it kept reading with the key it held.
 * Reported on 2026-09-10 as "they don't lose access".
 *
 * A revoked phone keeping its key is decision 18's whole reason rotation exists
 * — the SERVER cannot take the key back, only move away from it. This is the
 * honest device's half: the moment a status read says the bundle is on a key
 * this device does not hold, forget the one it does. `reconcileLocalKeys` owns
 * that decision and every guard around it (a rotation in flight, this device's
 * own mid-promotion window, a reset); this only makes sure it is asked on
 * EVERY status read rather than one path's.
 *
 * Single-flight: a status that lands while a reconcile is running is picked up
 * by the next one — the reconcile reads the store, not the argument, for
 * everything that matters.
 */
import { reconcileLocalKeys } from './reset';

/**
 * @param {{ store: object, reconcile?: typeof reconcileLocalKeys }} args
 * @returns {() => void} the unwatch
 */
export function installSignedOutWatcher({ store, reconcile = reconcileLocalKeys } = {}) {
  if (!store) throw new TypeError('installSignedOutWatcher needs the root Vuex store');

  let running = false;
  const check = status => {
    // Nothing to forget, or nothing read yet. `hasMasterKey` is what makes a
    // sign-out matter here at all; a keyless device has the approval screen.
    if (!status || store.getters['courses-keys/hasMasterKey'] !== true) return;
    if (running) return;
    running = true;
    Promise.resolve(reconcile({ store, status }))
      .catch(error => {
        console.warn('[CoursesE2EE] A status read could not be reconciled against the key:', error);
      })
      .finally(() => {
        running = false;
      });
  };

  return store.watch(() => store.state['courses-devices']?.status ?? null, check);
}
